import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewChild, ChangeDetectionStrategy, OutputRefSubscription, inject } from '@angular/core';
import { DiffEditorModel, DiffEditorComponent } from 'ngx-monaco-editor-v2';
import { FormsModule } from '@angular/forms';

import type { IDisposable } from 'monaco-editor';

@Component({
  selector: 'app-visual-compare',
  templateUrl: './visual-compare.component.html',
  styleUrls: ['./visual-compare.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, DiffEditorComponent]
})
export class VisualCompareComponent implements AfterViewInit, OnDestroy {
  @ViewChild(DiffEditorComponent, { static: true }) private diffEditorComp!: DiffEditorComponent;

  diffCount: number | null = null;
  private changeDetector = inject(ChangeDetectorRef);
  private diffEditorUpdateDisposable: IDisposable | null = null;
  private diffEditorOnInitSubscription: OutputRefSubscription | null = null;

  private valueLeft = '';
  @Input() get left(): string { return this.valueLeft; }
  set left(value: string) {
    this.valueLeft = value;
    this.valueLeftChange.emit(value);

    this.originalModel = { ...this.originalModel, code: value };
    this.diffCount = null; // reset diff count until the next diff update callback runs
  }
  @Output() public valueLeftChange = new EventEmitter<string>();

  private valueRight = '';
  @Input() get right(): string { return this.valueRight; }
  set right(value: string) {
    this.valueRight = value;
    this.valueRightChange.emit(value);

    this.modifiedModel = { ...this.modifiedModel, code: value };
    this.diffCount = null; // reset diff count until the next diff update callback runs
  }
  @Output() public valueRightChange = new EventEmitter<string>();

  private valueLanguage = 'plaintext';
  @Input() get language(): string { return this.valueLanguage; }
  set language(value: string) {
    this.valueLanguage = value;
    this.valueLanguageChange.emit(value);

    this.originalModel = { ...this.originalModel, language: value };
    this.modifiedModel = { ...this.modifiedModel, language: value };
    this.diffOptions = { ...this.diffOptions, language: value };
  }
  @Output() public valueLanguageChange = new EventEmitter<string>();

  private valueSideBySide = true;
  @Input() get sideBySide(): boolean { return this.valueSideBySide; }
  set sideBySide(value: boolean) {
    this.valueSideBySide = value;
    this.valueSideBySideChange.emit(value);

    this.diffOptions = { ...this.diffOptions, renderSideBySide: value };
  }
  @Output() public valueSideBySideChange = new EventEmitter<boolean>();

  languages = [
    { id: 'plaintext', name: 'Text' },
    { id: 'json', name: 'JSON' },
    { id: 'xml', name: 'XML' },
  ];

  sideBySideOptions = [
    { id: true, name: 'Side-by-side' },
    { id: false, name: 'Inline' },
  ];

  originalModel: DiffEditorModel = { code: this.left, language: this.language };
  modifiedModel: DiffEditorModel = { code: this.right, language: this.language };
  diffOptions = {
    theme: 'vs',
    language: this.language,
    readOnly: true,
    stickyScroll: { enabled: false },
    renderSideBySide: true,
    enableSplitViewResizing: false,
    automaticLayout: true,
    scrollBeyondLastLine: false
  };

  ngAfterViewInit(): void {
    this.diffEditorOnInitSubscription = this.diffEditorComp.onInit.subscribe((diffEditor: DiffEditorInstance) => {
      this.diffEditorUpdateDisposable?.dispose();
      this.diffEditorUpdateDisposable = diffEditor.onDidUpdateDiff(() => {
        const changes = diffEditor.getLineChanges();
        this.diffCount = changes ? changes.length : 0;

        // Monaco's onDidUpdateDiff callback runs outside Angular's zone, so Angular is unaware
        // of the state change and will not schedule change detection automatically. markForCheck()
        // explicitly marks this component as dirty.
        this.changeDetector.markForCheck();
      });
    });
  }

  ngOnDestroy(): void {
    this.diffEditorUpdateDisposable?.dispose();
    this.diffEditorOnInitSubscription?.unsubscribe();
  }
}

interface DiffEditorInstance {
  onDidUpdateDiff: (callback: () => void) => IDisposable;
  getLineChanges: () => unknown[] | null;
}
